import { Alert, Button, Card, CardContent, CardFooter, CardHeader, CardTitle, Input, Switch, Textarea } from '@ibsys/kit';
import { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IntegrationResponse } from '@/entities/integrations';
import { useUpdateIntegration } from '@/entities/integrations';
import { useHasPermission } from '@/entities/profile';
import { toastError, toastSuccess } from '@/shared/lib/utils/toast';

type Props = {
  integration: IntegrationResponse;
};

type FormValues = {
  name: string;
  description: string;
  isActive: boolean;
};

const buildDefaults = (integration: IntegrationResponse): FormValues => ({
  name: integration.name ?? '',
  description: integration.description ?? '',
  isActive: integration.isActive,
});

export const GeneralInfoTab: FC<Props> = ({ integration }) => {
  const { t } = useTranslation(['integrations', 'common']);
  const canAdmin = useHasPermission('ADMIN_INTEGRATION_INFO');
  const { mutateAsync, isPending } = useUpdateIntegration();

  const methods = useForm<FormValues>({ defaultValues: buildDefaults(integration) });
  const { control, handleSubmit, reset } = methods;

  useEffect(() => {
    reset(buildDefaults(integration));
  }, [integration, reset]);

  const onSubmit = async (values: FormValues) => {
    try {
      await mutateAsync({
        code: integration.code,
        payload: {
          name: values.name.trim(),
          description: values.description.trim() ? values.description.trim() : null,
          isActive: values.isActive,
        },
      });
      toastSuccess(t('integrations:general.saveSuccess'));
    } catch {
      toastError(t('integrations:general.saveError'));
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">{t('integrations:tabs.general')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 max-w-3xl">
            {/* Статус живой проверки соединения (только для активных интеграций с поддержкой проверки) */}
            {integration.connection &&
              (integration.connection.available ? (
                <Alert type="success" title={t('integrations:general.connection.available')} />
              ) : (
                <Alert
                  type="error"
                  title={t('integrations:general.connection.unavailable')}
                  description={integration.connection.detail ?? undefined}
                />
              ))}
            <Input
              control={control}
              name="name"
              label={t('integrations:general.fields.name')}
              disabled={!canAdmin}
              rules={{
                required: { value: true, message: t('integrations:general.validation.required') },
              }}
            />
            <Input label={t('integrations:general.fields.code')} value={integration.code} disabled readOnly />
            <Textarea
              control={control}
              name="description"
              label={t('integrations:general.fields.description')}
              disabled={!canAdmin}
              rows={4}
            />
            <Switch
              control={control}
              name="isActive"
              label={t('integrations:general.fields.isActive')}
              disabled={!canAdmin}
            />
          </CardContent>
          {canAdmin && (
            <CardFooter className="flex justify-end">
              <Button type="submit" disabled={isPending}>
                {t('integrations:general.saveButton')}
              </Button>
            </CardFooter>
          )}
        </Card>
      </form>
    </FormProvider>
  );
};
